import type { UserApiKey } from '@/common/db-typebox';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Input,
  Modal,
  Popconfirm,
  Table,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTreatyClient } from '../../lib/client';

export const ApiKeyPage = () => {
  const [keys, setKeys] = useState<UserApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const client = useTreatyClient();

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const response = await client.api.users.api_keys.list.get();
      if (response.data?.success) {
        setKeys(response.data.data);
      }
    } catch (error) {
      message.error('Failed to fetch API keys');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      message.warning('Please enter a valid key name');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await client.api.users.api_keys.create.post({
        name: newKeyName,
      });
      if (response.data) {
        message.success('API key created successfully');
        setCreateModalOpen(false);
        setNewKeyName('');
        fetchKeys();
      }
    } catch (error) {
      message.error('Failed to create API key');
      console.error(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteKey = async (id: string) => {
    try {
      const response = await client.api.users
        .api_keys({
          id,
        })
        .delete();
      if (response.data?.success) {
        message.success('API key deleted successfully');
        fetchKeys();
      }
    } catch (error) {
      message.error('Failed to delete API key');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'API Key',
      dataIndex: 'api_key',
      key: 'api_key',
      render: (text: string) => (
        <Typography.Text
          className="font-mono"
          copyable={{
            text,
          }}
        >
          {text.slice(0, 4)}...{text.slice(-4)}
        </Typography.Text>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (record: UserApiKey) => (
        <Popconfirm
          title="Are you sure you want to delete this API key?"
          description="This action cannot be undone and the key will no longer be usable"
          onConfirm={() => handleDeleteKey(record.id)}
          okText="Delete"
          cancelText="Cancel"
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Card
        title="API Key Management"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Create New Key
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={keys}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title="Create New API Key"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setNewKeyName('');
        }}
        onOk={handleCreateKey}
        confirmLoading={createLoading}
        okText="Create"
        cancelText="Cancel"
      >
        <div className="py-4">
          <label htmlFor="newKeyName" className="block mb-2 text-sm">
            Key Name
          </label>
          <Input
            id="newKeyName"
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder="Enter key name, e.g.: Development Environment"
          />
        </div>
      </Modal>
    </div>
  );
};
